// CancellationLinkModal — shows the raw cancellation URL ONCE so staff can
// copy it to clipboard, then clears it from component state on close. The
// raw token is intentionally not persisted (no React Query cache, no
// closure capture beyond the lifetime of the modal): the server stores
// only its SHA-256, so refreshing the modal yields a different URL.
//
// Per UI convention: no left-border accent strokes. Hierarchy comes from
// background colour + typography.

import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Copy } from 'lucide-react';
import { Modal } from '../../../components/ui/Modal/Modal';
import { Button } from '../../../components/ui/Button/Button';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { generateCancellationLink } from '../../../api/bookings';
import { useToast } from '../../../hooks/useToast';
import styles from './CancellationLinkModal.module.css';

export interface CancellationLinkModalProps {
  open: boolean;
  encId: string | null;
  reference?: string | null;
  onClose: () => void;
}

function formatExpiry(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function CancellationLinkModal({
  open,
  encId,
  reference,
  onClose,
}: CancellationLinkModalProps): JSX.Element | null {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  // Track the booking we last issued for so re-opens of the same modal don't
  // re-issue (which would rotate the token unnecessarily).
  const issuedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      // Clear from state on close so the raw token does not linger.
      setUrl(null);
      setExpiresAt(null);
      setCopied(false);
      setErrorMsg(null);
      setLoading(false);
      issuedForRef.current = null;
      return;
    }
    if (encId === null) return;
    if (issuedForRef.current === encId) return;
    issuedForRef.current = encId;

    let cancelled = false;
    (async () => {
      setLoading(true);
      setErrorMsg(null);
      try {
        const result = await generateCancellationLink(encId);
        if (cancelled) return;
        setUrl(result.url);
        setExpiresAt(result.expires_at);
      } catch (err) {
        if (cancelled) return;
        const message = axios.isAxiosError(err)
          ? (err.response?.data as { message?: string } | undefined)?.message
            ?? 'Could not generate cancellation link.'
          : 'Could not generate cancellation link.';
        setErrorMsg(message);
        toast.error(message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, encId, toast]);

  async function handleCopy() {
    if (url === null) return;
    try {
      if (
        typeof navigator !== 'undefined'
        && navigator.clipboard
        && typeof navigator.clipboard.writeText === 'function'
      ) {
        await navigator.clipboard.writeText(url);
      } else if (inputRef.current !== null) {
        inputRef.current.select();
        document.execCommand('copy');
      }
      setCopied(true);
      toast.success('Cancellation link copied to clipboard');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Could not copy — please copy the link manually.');
    }
  }

  if (!open) return null;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Cancellation link"
      size="md"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className={styles.body}>
        {reference ? (
          <p className={styles.subtitle}>Booking {reference}</p>
        ) : null}

        {loading ? (
          <div className={styles.empty}>
            <Spinner size="sm" /> Generating link…
          </div>
        ) : errorMsg !== null ? (
          <p role="alert" className={styles.empty}>
            {errorMsg}
          </p>
        ) : url !== null ? (
          <>
            <div className={styles.linkRow}>
              <input
                ref={inputRef}
                type="text"
                readOnly
                value={url}
                onFocus={(e) => e.currentTarget.select()}
                aria-label="Cancellation link URL"
                className={styles.linkInput}
              />
              <Button
                variant="primary"
                icon={Copy}
                onClick={() => void handleCopy()}
                className={styles.copyButton}
              >
                Copy
              </Button>
            </div>
            {copied ? (
              <span className={styles.copiedFlash} role="status">
                Copied
              </span>
            ) : null}
            <div className={styles.expiryNotice} role="note">
              <span className={styles.expiryLabel}>Expires</span>
              <span className={styles.expiryContent}>
                {expiresAt !== null ? formatExpiry(expiresAt) : '—'}
              </span>
              <span className={styles.expiryContent}>
                This link expires in 10 days or when the booking is completed
                or cancelled.
              </span>
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  );
}
